import React, { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck, X, Trash2 } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import DirhamSymbol from './DirhamSymbol';
import { useNavigate } from 'react-router-dom';

// Format date helper (no external dependency needed)
const formatDate = (date) => {
  if (!date) return '';
  try {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    return notificationDate.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const NotificationBell = ({ user }) => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification._id);
    }

    // Navigate based on notification type/related_type - but not for investors
    if (notification.related_type === 'payout' || notification.type === 'payout' || notification.type === 'alert') {
      // Only navigate to payout-alerts if user is not an investor
      if (user?.role !== 'investor') {
        navigate('/payout-alerts');
        setIsOpen(false);
      } else {
        // For investors, just close the dropdown without navigation
        setIsOpen(false);
      }
    }
  };


  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-primary-500 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-error-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-primary-800 rounded-lg shadow-xl border border-primary-200 dark:border-primary-700 z-[100] max-h-[calc(100vh-120px)] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-primary-200 dark:border-primary-700">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-50">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 flex items-center gap-1"
              >
                <CheckCheck size={16} />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1 min-h-0">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-primary-500 dark:text-primary-400">
                <Bell size={48} className="mx-auto mb-2 opacity-50" />
                <p>No notifications</p>
              </div>
            ) : (
              <>
                {unreadNotifications.length > 0 && (
                  <div>
                    <div className="px-4 py-2 text-xs font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/50">
                      Unread
                    </div>
                    {unreadNotifications.map((notification) => (
                      <NotificationItem
                        key={notification._id}
                        notification={notification}
                        onClick={() => handleNotificationClick(notification)}
                        onDelete={() => deleteNotification(notification._id)}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                )}

                {readNotifications.length > 0 && (
                  <div>
                    {unreadNotifications.length > 0 && (
                      <div className="px-4 py-2 text-xs font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/50">
                        Read
                      </div>
                    )}
                    {readNotifications.map((notification) => (
                      <NotificationItem
                        key={notification._id}
                        notification={notification}
                        onClick={() => handleNotificationClick(notification)}
                        onDelete={() => deleteNotification(notification._id)}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const NotificationItem = ({ notification, onClick, onDelete, formatDate }) => {
  const isUnread = !notification.read;

  return (
    <div
      className={`p-4 border-b border-primary-100 dark:border-primary-700/50 hover:bg-primary-50 dark:hover:bg-primary-900/50 transition-colors cursor-pointer ${
        isUnread ? 'bg-primary-50/50 dark:bg-primary-900/30' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4
              className={`text-sm font-semibold ${
                isUnread
                  ? 'text-primary-900 dark:text-primary-50'
                  : 'text-primary-700 dark:text-primary-300'
              }`}
            >
              {notification.title}
            </h4>
            {isUnread && (
              <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1.5" />
            )}
          </div>
          <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
            {notification.message}
          </p>
          {notification.data?.amount && (
            <div className="flex items-center gap-1 mt-2 text-sm font-medium text-primary-700 dark:text-primary-300">
              <DirhamSymbol size={14} inline />
              {notification.data.amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </div>
          )}
          <p className="text-xs text-primary-500 dark:text-primary-500 mt-2">
            {formatDate(notification.createdAt)}
          </p>
        </div>
        <div className="flex items-start gap-1 flex-shrink-0">
          {isUnread && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              className="p-1 rounded hover:bg-primary-200 dark:hover:bg-primary-700 text-primary-600 dark:text-primary-400"
              aria-label="Mark as read"
            >
              <Check size={16} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 rounded hover:bg-error-100 dark:hover:bg-error-900/50 text-error-600 dark:text-error-400"
            aria-label="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationBell;
